import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../../config/api';
import { Plus, Trash2, MapPin, Clock, User, Loader2, Camera, ChevronDown, ChevronUp, Pencil, Check, X, Navigation } from 'lucide-react';
import { Button } from '../../components/ui/button';
import 'leaflet/dist/leaflet.css';

const getAdminAuth = () => sessionStorage.getItem('adminPassword') || '';
const getAuthHeaders = () => ({ 'Authorization': getAdminAuth() });

// Leaflet map component with GPS trail support
const TrackingMap = ({ tracking, gpsTrails }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const trailsRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;
    import('leaflet').then(L => {
      if (cancelled || !mapRef.current) return;
      if (mapInstanceRef.current) mapInstanceRef.current.remove();
      const map = L.map(mapRef.current).setView([51.5074, -0.1278], 11);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap'
      }).addTo(map);
      mapInstanceRef.current = map;
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      });
      updateMap(L, map);
    });
    return () => { cancelled = true; if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    let cancelled = false;
    import('leaflet').then(L => {
      if (cancelled || !mapInstanceRef.current) return;
      updateMap(L, mapInstanceRef.current);
    });
    return () => { cancelled = true; };
  }, [tracking, gpsTrails]);

  const updateMap = (L, map) => {
    // Clear old markers and trails
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    trailsRef.current.forEach(t => t.remove());
    trailsRef.current = [];
    const bounds = [];

    // Draw GPS trails
    Object.entries(gpsTrails || {}).forEach(([workerId, points]) => {
      if (!points || points.length < 2) return;
      const coords = points.map(p => [p.latitude, p.longitude]);
      const trail = L.polyline(coords, { color: '#1a1f4e', weight: 3, opacity: 0.5, dashArray: '8, 6' }).addTo(map);
      trailsRef.current.push(trail);
      coords.forEach(c => bounds.push(c));
    });

    // Draw markers
    tracking.forEach(t => {
      if (!t.location) return;
      const { latitude, longitude } = t.location;
      bounds.push([latitude, longitude]);
      const isActive = !!t.active_visit;
      const icon = L.divIcon({
        html: `<div style="background:${isActive ? '#00c853' : '#6b7280'};width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;">${t.worker.name.charAt(0)}</div>`,
        className: '', iconSize: [32, 32], iconAnchor: [16, 16],
      });
      const popup = `<div style="min-width:150px"><b>${t.worker.name}</b><br/>${isActive ? `<span style="color:#00c853">Cleaning: ${t.active_visit.site_name}</span><br/>` : '<span style="color:#888">Idle</span><br/>'}<small>Updated: ${new Date(t.location.timestamp).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</small></div>`;
      const marker = L.marker([latitude, longitude], { icon }).addTo(map).bindPopup(popup);
      markersRef.current.push(marker);
    });

    if (bounds.length > 0) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
  };

  return <div ref={mapRef} style={{ height: '400px', width: '100%', borderRadius: '12px' }} />;
};

const WorkerManagement = () => {
  const [workers, setWorkers] = useState([]);
  const [tracking, setTracking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tracking');
  const [visits, setVisits] = useState([]);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [visitPhotos, setVisitPhotos] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState(null);
  const [editingWorker, setEditingWorker] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [gpsTrails, setGpsTrails] = useState({});
  const [etas, setEtas] = useState({});
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => { loadTracking(); loadEtas(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([loadWorkers(), loadTracking(), loadTodayVisits(), loadGeofenceAlerts()]);
    setLoading(false);
  };

  const loadWorkers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/workers`, { headers: getAuthHeaders() });
      const data = await res.json();
      setWorkers(data.workers || []);
    } catch {}
  };

  const loadTracking = async () => {
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/tracking/live`, { headers: getAuthHeaders() });
      const data = await res.json();
      setTracking(data.tracking || []);
      // Load GPS trails and ETAs for workers with locations
      const workerIds = (data.tracking || []).filter(t => t.location).map(t => t.worker.id);
      loadGpsTrails(workerIds);
      loadEtasForWorkers(workerIds);
    } catch {}
  };

  const loadGpsTrails = async (workerIds) => {
    const today = new Date().toISOString().split('T')[0];
    const trails = {};
    await Promise.all(workerIds.map(async (wid) => {
      try {
        const res = await fetch(`${API_URL}/api/worker/admin/workers/${wid}/gps-history?date=${today}`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (data.points && data.points.length > 0) trails[wid] = data.points;
      } catch {}
    }));
    setGpsTrails(trails);
  };

  const loadEtasForWorkers = async (workerIds) => {
    const newEtas = {};
    await Promise.all(workerIds.map(async (wid) => {
      try {
        const res = await fetch(`${API_URL}/api/worker/admin/workers/${wid}/eta`, { headers: getAuthHeaders() });
        const data = await res.json();
        newEtas[wid] = data;
      } catch {}
    }));
    setEtas(newEtas);
  };

  const loadGeofenceAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/geofence-alerts`, { headers: getAuthHeaders() });
      const data = await res.json();
      setGeofenceAlerts(data.alerts || []);
    } catch {}
  };


  const loadEtas = async () => {
    const workerIds = tracking.filter(t => t.location).map(t => t.worker.id);
    if (workerIds.length) loadEtasForWorkers(workerIds);
  };

  const loadTodayVisits = async () => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/visits?date=${today}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setVisits(data.visits || []);
    } catch {}
  };

  const loadVisitPhotos = async (visitId) => {
    if (visitPhotos[visitId]) { setExpandedVisit(expandedVisit === visitId ? null : visitId); return; }
    setLoadingPhotos(visitId);
    setExpandedVisit(visitId);
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/visits/${visitId}/photos`, { headers: getAuthHeaders() });
      const data = await res.json();
      setVisitPhotos(prev => ({ ...prev, [visitId]: data.photos || [] }));
    } catch {}
    finally { setLoadingPhotos(null); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/workers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(form)
      });
      if (res.ok) { setForm({ name: '', email: '', password: '', phone: '' }); setShowForm(false); loadWorkers(); }
      else { const data = await res.json(); alert(data.detail || 'Failed'); }
    } catch { alert('Connection error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this worker?')) return;
    await fetch(`${API_URL}/api/worker/admin/workers/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    loadWorkers();
  };

  const startEdit = (w) => {
    setEditingWorker(w.id);
    setEditForm({ name: w.name, email: w.email, password: '', phone: w.phone || '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const payload = { name: editForm.name, email: editForm.email, phone: editForm.phone };
      if (editForm.password) payload.password = editForm.password;
      const res = await fetch(`${API_URL}/api/worker/admin/workers/${editingWorker}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      if (res.ok) { setEditingWorker(null); loadWorkers(); }
      else { const data = await res.json(); alert(data.detail || 'Failed'); }
    } catch { alert('Connection error'); }
    finally { setEditSaving(false); }
  };

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return (
    <div className="p-6 lg:p-8"><div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-48"></div><div className="h-32 bg-gray-200 rounded"></div></div></div>
  );

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Workers</h1>
          <p className="text-gray-500 text-sm">{workers.length} worker{workers.length !== 1 ? 's' : ''} &middot; GPS tracking live</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="add-worker-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Worker
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['tracking', 'workers', 'visits', 'alerts'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${activeTab === tab ? 'bg-[#1a1f4e] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`worker-tab-${tab}`}>
            {tab === 'tracking' ? 'Live Tracking' : tab === 'workers' ? 'Manage Workers' : tab === 'visits' ? "Today's Visits" : 'Geofence Alerts'}
            {tab === 'alerts' && geofenceAlerts.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{geofenceAlerts.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Add Worker Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">New Worker</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none" data-testid="worker-name-input" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none" data-testid="worker-phone-input" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none" data-testid="worker-email-input" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input type="text" required value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Set their login password"
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none" data-testid="worker-password-input" /></div>
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="save-worker-btn">{saving ? 'Saving...' : 'Create Worker'}</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* LIVE TRACKING TAB */}
      {activeTab === 'tracking' && (
        <div className="space-y-4">
          {tracking.some(t => t.location) ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <TrackingMap tracking={tracking} gpsTrails={gpsTrails} />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No GPS data yet — map will appear when workers start tracking</p>
            </div>
          )}

          {tracking.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">No active workers</div>
          ) : tracking.map(t => {
            const eta = etas[t.worker.id];
            return (
            <div key={t.worker.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${t.active_visit ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <User className={`w-5 h-5 ${t.active_visit ? 'text-green-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t.worker.name}</p>
                    <p className="text-sm text-gray-500">{t.worker.phone || t.worker.email}</p>
                  </div>
                </div>
                {t.active_visit && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> Cleaning
                  </span>
                )}
              </div>
              {t.active_visit && (
                <div className="bg-green-50 rounded-lg p-3 mb-3">
                  <p className="text-sm font-medium text-green-800">Currently at: {t.active_visit.site_name}</p>
                  <p className="text-xs text-green-600">{t.active_visit.site_address} &middot; Started {formatTime(t.active_visit.started_at)}</p>
                </div>
              )}
              {/* ETA to next block */}
              {eta && eta.eta && (
                <div className="bg-blue-50 rounded-lg p-3 mb-3 flex items-center gap-3">
                  <Navigation className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Next: {eta.next_site?.site_name}</p>
                    <p className="text-xs text-blue-600">~{eta.eta.minutes} min ({eta.eta.distance_km} km away)</p>
                  </div>
                </div>
              )}
              {eta && !eta.eta && eta.next_site && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3 flex items-center gap-3">
                  <Navigation className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Next: {eta.next_site.site_name}</p>
                    <p className="text-xs text-gray-500">{eta.message}</p>
                  </div>
                </div>
              )}
              {t.location && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="w-4 h-4" />
                  <span>Last seen: {t.location.latitude.toFixed(5)}, {t.location.longitude.toFixed(5)}</span>
                  <Clock className="w-3 h-3 ml-2" />
                  <span>{formatTime(t.location.timestamp)}</span>
                </div>
              )}
              {t.day_plan && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-1">Today's plan:</p>
                  <div className="flex flex-wrap gap-1">
                    {t.day_plan.sites.map((s, i) => (
                      <span key={i} className={`text-xs px-2 py-1 rounded ${
                        t.active_visit?.site_id === s.site_id ? 'bg-green-100 text-green-700 font-medium' : 'bg-gray-100 text-gray-600'
                      }`}>{i + 1}. {s.site_name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );})}
        </div>
      )}

      {/* MANAGE WORKERS TAB */}
      {activeTab === 'workers' && (
        <div className="space-y-3">
          {workers.map(w => (
            <div key={w.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {editingWorker === w.id ? (
                <form onSubmit={handleEdit} className="p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                      <input type="text" required value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}
                        className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm" data-testid="edit-worker-name" /></div>
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                      <input type="email" required value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})}
                        className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm" data-testid="edit-worker-email" /></div>
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                      <input type="tel" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})}
                        className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm" data-testid="edit-worker-phone" /></div>
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">New Password <span className="text-gray-400">(leave blank to keep)</span></label>
                      <input type="text" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})}
                        placeholder="Enter new password"
                        className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm" data-testid="edit-worker-password" /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={editSaving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="save-edit-worker-btn">
                      {editSaving ? 'Saving...' : <><Check className="w-4 h-4 mr-1" /> Save</>}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setEditingWorker(null)}>
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1a1f4e]/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#1a1f4e]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{w.name}</p>
                      <p className="text-sm text-gray-500">{w.email} {w.phone && `· ${w.phone}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(w)} className="p-2 text-gray-400 hover:text-[#1a1f4e] transition-colors" data-testid={`edit-worker-${w.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(w.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-worker-${w.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TODAY'S VISITS TAB */}
      {activeTab === 'visits' && (
        <div className="space-y-3">
          {visits.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">No visits today</div>
          ) : visits.map(v => (
            <div key={v.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`visit-card-${v.id}`}>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{v.site_name}</p>
                    <p className="text-sm text-gray-500">{v.worker_name} &middot; {v.site_address}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${v.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {v.status === 'completed' ? 'Done' : 'In Progress'}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-gray-500">Started: {formatTime(v.started_at)}</span>
                  {v.finished_at && <span className="text-xs text-gray-500">Finished: {formatTime(v.finished_at)}</span>}
                  <button onClick={() => loadVisitPhotos(v.id)}
                    className="ml-auto flex items-center gap-1 text-xs text-[#1a1f4e] hover:underline font-medium"
                    data-testid={`visit-photos-toggle-${v.id}`}>
                    {loadingPhotos === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                    {(v.before_photos?.length || 0) + (v.after_photos?.length || 0)} photo{((v.before_photos?.length || 0) + (v.after_photos?.length || 0)) === 1 ? '' : 's'}
                    {expandedVisit === v.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                </div>
              </div>
              {expandedVisit === v.id && visitPhotos[v.id] && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  {visitPhotos[v.id].length === 0 ? (
                    <p className="text-sm text-gray-400">No photos uploaded</p>
                  ) : (
                    <div className="space-y-3">
                      {['before', 'after'].map(type => {
                        const typePhotos = visitPhotos[v.id].filter(p => p.photo_type === type);
                        if (!typePhotos.length) return null;
                        return (
                          <div key={type}>
                            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${type === 'before' ? 'text-amber-600' : 'text-green-600'}`}>{type} ({typePhotos.length})</p>
                            <div className="grid grid-cols-4 gap-2">
                              {typePhotos.map(p => (
                                <img key={p.id} src={`${API_URL}/api/worker/photos/${p.storage_path}?admin_key=${getAdminAuth()}`}
                                  alt={type} className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80"
                                  loading="lazy" onError={(e) => { e.target.style.display = 'none'; }}
                                  onClick={() => window.open(`${API_URL}/api/worker/photos/${p.storage_path}?admin_key=${getAdminAuth()}`, '_blank')} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* GEOFENCE ALERTS TAB */}
      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {geofenceAlerts.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No geofence alerts today</p>
              <p className="text-xs mt-1">Alerts appear when workers arrive at or leave a block area</p>
            </div>
          ) : geofenceAlerts.map(a => (
            <div key={a.id} data-testid={`geofence-alert-${a.id}`} className={`bg-white rounded-xl border p-4 flex items-center gap-4 ${
              a.alert_type === 'arrived' ? 'border-green-200' : 'border-amber-200'
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                a.alert_type === 'arrived' ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                <MapPin className={`w-5 h-5 ${a.alert_type === 'arrived' ? 'text-green-600' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {a.worker_name} {a.alert_type === 'arrived' ? 'arrived at' : 'left'} {a.site_name}
                </p>
                <p className="text-sm text-gray-500">
                  {formatTime(a.timestamp)} &middot; {a.distance_m}m {a.alert_type === 'arrived' ? 'from site' : 'away'}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                a.alert_type === 'arrived' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {a.alert_type === 'arrived' ? 'Arrived' : 'Left'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkerManagement;
